<template>
  <Teleport to="body">
    <Transition name="error-flash">
      <!-- aria-hidden: the strip and the gun already say what was refused; this
           is the same fact for peripheral vision. -->
      <div v-if="visible" class="error-flash" aria-hidden="true" />
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { onBeforeUnmount, ref, watch } from 'vue'
import { useScreenFlash } from '~/composables/useScreenFlash'

/**
 * A red flash around the edge of the screen when a scan is refused — the
 * sibling of AttentionGlow (blue, ten seconds, pulsing) with the opposite
 * temperament: it is over in a couple of seconds and does not breathe. A
 * driver whose eyes are on the pallet, not the tablet, sees the light change
 * at the same moment the gun goes red (user, 2026-09-24).
 *
 * Raised by signalScanner('error') through useScreenFlash, so every refusal
 * that reddens the gun reddens the screen, and no screen has to wire it.
 * Re-triggering while visible restarts the clock. Never in the way:
 * pointer-events none, and a tap dismisses it early.
 */
const FLASH_MS = 2_000

const { errorFlashes } = useScreenFlash()
const visible = ref(false)
let timer: ReturnType<typeof setTimeout> | null = null

function dismiss() {
  if (timer !== null) {
    clearTimeout(timer)
    timer = null
  }
  window.removeEventListener('pointerdown', dismiss, true)
  visible.value = false
}

function show() {
  dismiss()
  visible.value = true
  timer = setTimeout(dismiss, FLASH_MS)
  window.addEventListener('pointerdown', dismiss, { capture: true, passive: true })
}

watch(errorFlashes, (count) => {
  if (count > 0) show()
})

onBeforeUnmount(dismiss)
</script>

<style scoped>
.error-flash {
  position: fixed;
  inset: 0;
  pointer-events: none;
  /* Same layer as the attention glow: above the bar and the scanner modal,
     under the dialogs and the toaster. */
  z-index: 100;
  /* Transparent through the middle, red at the rim, like the glow — the
     content stays readable and the refusal reads as coming from the bezel. */
  background: radial-gradient(ellipse at center, transparent 52%, var(--color-error-flash) 100%);
}

/* Fast in, a hold, and out — a flash, not a glow. */
.error-flash-enter-active {
  transition: opacity 120ms ease-out;
}
.error-flash-leave-active {
  transition: opacity 500ms ease-in;
}
.error-flash-enter-from,
.error-flash-leave-to {
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .error-flash-enter-active,
  .error-flash-leave-active {
    transition: none;
  }
}
</style>
